import { defineStore } from "pinia";
import { callApi } from "src/utils/apiRequestHandler";
import { usePersistedStore } from "src/stores/persisted-store";
import { identityAxiosInstance, setidentityAxiosHeaders } from "src/boot/axios";
import { decodeJwtToken, inIframe } from "src/utils";
import { showError } from "src/utils/notify";
import { Platform } from "quasar";
import firebase from "firebase/app";
import "firebase/messaging";
import {
  getAccountID,
  getFirebaseConfigForWhiteLabel,
} from "src/utils/commonFunction";

//shared so the header and profile mounting together make one request
let phoneLinkSettingsRequest = null;

// Token lookups never block for long: token save and logout await them, so a
// callback that does not arrive must not hold either up.
const TOKEN_LOOKUP_TIMEOUT_MS = 3000;

// Klaviyo sends iOS pushes through APNs only, so an iOS device must also be
// registered with its raw APNs token. The FCM token keeps serving our own pushes.
const getApnsToken = () =>
  new Promise((resolve) => {
    if (
      !Platform.is?.cordova ||
      !Platform.is?.ios ||
      typeof FirebasePlugin === "undefined"
    ) {
      resolve(null);
      return;
    }
    const timer = setTimeout(() => resolve(null), TOKEN_LOOKUP_TIMEOUT_MS);
    const done = (token) => {
      clearTimeout(timer);
      resolve(token || null);
    };
    FirebasePlugin.getAPNSToken(done, () => done(null));
  });

// The FCM token this device is registered with. persistedStore.firebaseOldToken
// is empty after the app is killed and reopened (that store is not restored),
// so logout asks Firebase again - the token does not change for an install.
const getCurrentFcmToken = () =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), TOKEN_LOOKUP_TIMEOUT_MS);
    const done = (token) => {
      clearTimeout(timer);
      resolve(token || null);
    };
    try {
      if (Platform.is?.cordova) {
        if (typeof FirebasePlugin === "undefined") return done(null);
        FirebasePlugin.getToken(done, () => done(null));
      } else if (
        !inIframe() &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted" &&
        firebase.apps.length
      ) {
        // Only when permission was already granted: never prompts at logout.
        firebase
          .messaging()
          .getToken()
          .then(done, () => done(null));
      } else {
        done(null);
      }
    } catch (error) {
      done(null);
    }
  });

export const useUserInfo = defineStore("user", {
  state: () => ({
    user_info: {},
    loadUserInfo: false,
    consent_topics: [],
    loadConsentTopics: false,
    consent_settings: {},
    exit_question_list: [],
    loadExitQuestion: false,
    balances: [],
    pollUpdate: {},
    earningHistory: [],
    earningHistoryLoad: false,
    tmpUserDetails: {},
    userInLns: true,
    //store switches for link-based phone verification (white-label store
    //details: text_message_enable and enable_phone_link_verification)
    phoneLinkVerificationEnabled: false,
    phoneLinkSettingsLoaded: false,
  }),
  getters: {
    getProfileInfo: (state) => state.user_info,
    getUserBalancesList: (state) => state.balances,
    //white-label only (the marketplace has no account id). Only an explicit
    //0 counts: a response without the flag, or no phone on file, must not nag
    isPhoneUnverified: (state) =>
      !!getAccountID() &&
      state.phoneLinkVerificationEnabled &&
      state.loadUserInfo &&
      !!state.user_info?.phone &&
      //phone_link_verified == 0, and only that: a missing, null, empty or false
      //value never shows the prompts ("0" covers a string from the API)
      [0, "0"].includes(state.user_info?.phone_link_verified),
  },
  actions: {
    async getUserDetails() {
      let data = await callApi(() =>
        this.$request.get(this.apiEndpoints.USER_INFO)
      );
      if (data.data) {
        this.user_info = data.data.item;
        this.loadUserInfo = true;
      }
      return data.data;
    },
    //both store switches must be on: texting enabled and link verification
    //enabled. They sit under loyalty_options in the store details response
    setPhoneLinkVerificationSettings(storeItem) {
      const options = storeItem?.loyalty_options || {};
      this.phoneLinkVerificationEnabled =
        options.text_message_enable == 1 &&
        options.enable_phone_link_verification == 1;
      this.phoneLinkSettingsLoaded = true;
    },
    //reads the switches from the white-label store details once; the
    //marketplace has no account id and never asks
    async loadPhoneLinkVerificationSettings() {
      const accountId = getAccountID();
      if (!accountId || this.phoneLinkSettingsLoaded) return;

      if (!phoneLinkSettingsRequest) {
        phoneLinkSettingsRequest = this.$request
          .get(`${this.apiEndpoints.STORE_DETAILS(accountId)}?acc=${accountId}`)
          .then((res) => {
            if (res?.data?.item)
              this.setPhoneLinkVerificationSettings(res.data.item);
          })
          .catch(() => {})
          .finally(() => {
            phoneLinkSettingsRequest = null;
          });
      }
      return phoneLinkSettingsRequest;
    },
    async sendVerifyPhoneLink() {
      try {
        //acc goes in the body, not the query string; with it already set the
        //axios interceptor leaves the request alone
        const response = await this.$request.post(
          this.apiEndpoints.VALIDATE_PHONE,
          { acc: getAccountID() }
        );
        return response.data;
      } catch (error) {
        return { status: false };
      }
    },
    async userAvailableInLns(cst) {
      // VERIFY_CUSTOMER
      let data = await callApi(() =>
        this.$request.get(this.apiEndpoints.VERIFY_CUSTOMER(cst))
      );
      return data.data;
    },
    async addUserInLNSWithFTXData(profileData) {
      let data = await callApi(() =>
        this.$request.post(
          this.apiEndpoints.NEW_REGISTER_FROM_IDENTITY,
          profileData
        )
      );
      if (data.data && data.data.status) {
        return data.data;
      } else {
        return data.data;
      }
    },
    async updateUserProfileDetails(profileData) {
      let data = await callApi(() =>
        this.$request.post(this.apiEndpoints.PROFILE_UPDATE, profileData)
      );
      if (data.data && data.data.status) {
        this.user_info = data.data.item;
        return data.data;
      } else {
        return data.data;
      }
    },
    async updateUserProfileImage(formData) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.PROFILE_UPDATE,
          formData
        );
        if (data.data && data.data.status) {
          this.user_info = data.data.item;
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {}
    },
    async getUserDetailsById(id) {
      try {
        let data = await this.$request.get(
          this.apiEndpoints.USER_INFO_BY_ID(id)
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async blockUser(id) {
      try {
        let data = await this.$request.get(this.apiEndpoints.BLOCK_USER(id));
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async unFriend(id) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.FRIEND_UNFRIEND(id)
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async customerFeedback(params) {
      try {
        const data = await this.$request.post(
          this.apiEndpoints.CUSTOMER_FEEDBACK,
          {
            platform: params.platform,
            notes: params.notes,
            rating: params.rating,
          }
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async getConsentTopics() {
      try {
        this.consent_topics = [];
        this.loadConsentTopics = false;
        this.consent_settings = {};
        let data = await this.$request.get(
          this.apiEndpoints.GET_CONSENT_TOPICS
        );
        if (data.data) {
          this.consent_topics = data.data.items;
          this.loadConsentTopics = true;
          this.consent_settings = data.data.settings;
        }
        return data.data;
      } catch (error) {
        return false;
      }
    },
    async setConsentTopics(params) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.SAVE_CONSENT_PREF,
          params
        );
        if (data.data) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async deleteAccount() {
      try {
        let data = await this.$request.post(this.apiEndpoints.DELETE_AC);
        if (data.data) {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async getExitQuestions() {
      try {
        this.exit_question_list = [];
        this.loadExitQuestion = false;
        let data = await this.$request.get(
          this.apiEndpoints.DELETE_AC_EXIT_QUESTION
        );
        if (data.data) {
          this.exit_question_list = data.data.items;
          this.loadExitQuestion = true;
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    async saveExitQuestions(params) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.SAVE_EXIT_QUESTION,
          params
        );
        if (data.data && data.data.status) {
          return true;
        }
        return false;
      } catch (error) {
        return false;
      }
    },
    setUserBalancesList(balances) {
      this.balances = balances;
    },
    async fetchUserBalances(params, options) {
      this.balances = [];
      let URL, response;
      switch (options.category) {
        case "store":
          URL = this.apiEndpoints.GET_USER_BALANCE;
          break;
        case "mfg":
          URL = this.apiEndpoints.MFG_USER_BALANCE;
          break;
      }
      if (URL) {
        response = await this.$request.post(URL, params);
        if (response.data && response.data.status && response.data.items.length)
          this.balances = response.data.items;
        return response.data;
      } else {
        return {};
      }
    },
    async acceptConsent(id, type, platform_type) {
      try {
        const data = await this.$request.post(
          this.apiEndpoints.USER_ACCEPT_CONSENT(id),
          { platform_type: platform_type, type: type }
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async getConsentContent(type, platform_type) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.GET_CONSENT_CONTENT,
          { type: type, platform_type: platform_type }
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async getPollUpdate() {
      try {
        let data = await this.$request.get(this.apiEndpoints.POLL_UPDATE);
        if (data.data) {
          this.pollUpdate = data.data;
          return data.data;
        } else {
          return { status: false, message: error.message };
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async logOutApi() {
      try {
        const persistedStore = usePersistedStore();
        // Looked up together so logout waits at most one lookup timeout.
        const [regToken, apnsToken] = await Promise.all([
          persistedStore.firebaseOldToken || getCurrentFcmToken(),
          getApnsToken(),
        ]);
        const params = regToken ? { reg_token: regToken } : {};

        params.device_type = Platform.is.cordova
          ? Platform.is.android
            ? "Android"
            : "iOS"
          : "Web";
        if (apnsToken) params.apns_token = apnsToken;
        const data = await this.$request.post(this.apiEndpoints.LOGOUT, params);
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async getEarningHistory(params) {
      try {
        if (params.page === 1) {
          this.earningHistory = [];
          this.earningHistoryLoad = false;
        }
        const data = await this.$request.get(
          this.apiEndpoints.EARNING_HISTORY_USER(params.page, params.per_page)
        );
        if (data.data) {
          Array.prototype.push.apply(this.earningHistory, data.data.items);
          this.earningHistoryLoad = true;
        }
        return data.data;
      } catch (error) {}
    },
    resetEarningData() {
      this.earningHistory = [];
      this.earningHistoryLoad = false;
    },
    async setfcmTokenForUser(params) {
      try {
        if (getAccountID()) {
          let firebaseConfig = getFirebaseConfigForWhiteLabel();
          if (
            Object.keys(firebaseConfig).length &&
            firebaseConfig?.projectId?.length
          ) {
            params.project_id = firebaseConfig?.projectId;
          }
        }

        params.device_type = Platform.is.cordova
          ? Platform.is.android
            ? "Android"
            : "iOS"
          : "Web";
        const apnsToken = await getApnsToken();
        if (apnsToken) params.apns_token = apnsToken;
        const data = await this.$request.post(
          this.apiEndpoints.ASSOCIATE_REG_TOKEN,
          params
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async checkUserInLNS() {
      const persistedStore = usePersistedStore();
      if (!persistedStore?.user?.access_token) return;

      let decodeToken = decodeJwtToken(persistedStore?.user?.access_token),
        cst = decodeToken?.cst,
        sub = decodeToken?.sub;

      if (cst && cst.length == 0) {
        showError("Please use signup to access the portal.");
        setTimeout(() => {
          persistedStore.logout(router);
        }, 5000);
      } else {
        setidentityAxiosHeaders(persistedStore?.user?.access_token);
        const usrInLns = await this.userAvailableInLns(cst);
        if (!usrInLns?.status) {
          this.userInLns = false;
          try {
            await identityAxiosInstance.get(`v1/Account`).then((response) => {
              // this.tmpUserDetails = {};
              if (response?.data?.succeeded && response?.data?.data) {
                let resData = response?.data?.data;
                this.tmpUserDetails = {
                  cst: cst,
                  sub: sub,
                  firstName: resData.firstName,
                  lastName: resData.lastName,
                  email: resData.email,
                  countryCode: resData.countryCode,
                  phoneNumber: resData.phoneNumber,
                  dob: resData.dob,
                };
              } else {
                persistedStore.logout();
              }
            });
          } catch (error) {
            persistedStore.logout();
          }
        }
      }
    },
    async getEmailPreferences(email) {
      try {
        const data = await this.$request.get(
          this.apiEndpoints.EMAIL_PREFERENCES(email)
        );
        return data.data;
      } catch (error) {}
    },
    async setUnsubscribeUser(params) {
      try {
        const data = await this.$request.post(
          this.apiEndpoints.UNSUBSCRIBE,
          params
        );
        if (data.data) {
          return data.data;
        }
      } catch (error) {}
    },
    async getAppVersion() {
      if (Platform.is?.cordova) {
        try {
          const data = await this.$request.post(this.apiEndpoints.APP_VERSION, {
            platform: Platform?.is?.android ? "android_config" : "ios_config",
          });
          if (data.data) {
            return data.data;
          }
        } catch (error) {
          return { status: false };
        }
      }
    },
  },
});
